/**
 * TanStack Query hooks for `advertiser-campaign-upload`. Thin wrappers over
 * ./api. Query-keys follow the canonical `['advertiser-campaign-upload', ...]`
 * namespace so invalidations cascade across list + admin queue views.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	adminQueue,
	advertiserCampaignUploadQueryKeys,
	approve,
	listByAdvertiser,
	reject,
	submit,
	type CampaignUploadRow,
	type ListResponse,
	type RejectBody,
	type SubmitBody,
} from "./api";

export function useAdvertiserCampaignUploadList(
	advertiserId: string,
): UseQueryResult<ListResponse, Error> {
	return useQuery({
		queryKey: advertiserCampaignUploadQueryKeys.list(advertiserId),
		queryFn: () => listByAdvertiser(advertiserId),
		enabled: advertiserId.trim().length > 0,
	});
}

export function useAdvertiserCampaignUploadQueue(): UseQueryResult<
	ListResponse,
	Error
> {
	return useQuery({
		queryKey: advertiserCampaignUploadQueryKeys.adminQueue(),
		queryFn: adminQueue,
	});
}

export function useAdvertiserCampaignUploadSubmit(): UseMutationResult<
	CampaignUploadRow,
	Error,
	SubmitBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: submit,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: advertiserCampaignUploadQueryKeys.all,
			});
		},
	});
}

export interface ApproveArgs {
	id: string;
	reviewerId?: string;
}

export function useAdvertiserCampaignUploadApprove(): UseMutationResult<
	CampaignUploadRow,
	Error,
	ApproveArgs
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, reviewerId }: ApproveArgs) => approve(id, reviewerId),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: advertiserCampaignUploadQueryKeys.all,
			});
		},
	});
}

export interface RejectArgs {
	id: string;
	body: RejectBody;
	reviewerId?: string;
}

export function useAdvertiserCampaignUploadReject(): UseMutationResult<
	CampaignUploadRow,
	Error,
	RejectArgs
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body, reviewerId }: RejectArgs) =>
			reject(id, body, reviewerId),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: advertiserCampaignUploadQueryKeys.all,
			});
		},
	});
}
